"use client";

import { applyThemeMode, getStoredThemeMode, isThemeMode, type ThemeMode } from "@/features/theme/theme";
import { createContext, useCallback, useContext, useMemo, useSyncExternalStore } from "react";

interface ThemeContextValue {
    themeMode: ThemeMode;
    setThemeMode: ( themeMode: ThemeMode ) => void;
    toggleThemeMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>( undefined );
const THEME_CHANGE_EVENT = "predik-theme-change";

function subscribeThemeMode( onThemeModeChange: () => void ) {
    window.addEventListener( THEME_CHANGE_EVENT, onThemeModeChange );

    return () => {
        window.removeEventListener( THEME_CHANGE_EVENT, onThemeModeChange );
    };
}

function getThemeModeSnapshot(): ThemeMode {
    const currentThemeMode = document.documentElement.dataset.theme;
    if ( isThemeMode( currentThemeMode ) ) {
        return currentThemeMode;
    }

    return getStoredThemeMode();
}

function getServerThemeModeSnapshot(): ThemeMode {
    return "light";
}

function notifyThemeModeChange() {
    window.dispatchEvent( new Event( THEME_CHANGE_EVENT ) );
}

export function ThemeProvider( { children }: { children: React.ReactNode } ) {
    const themeMode = useSyncExternalStore(
        subscribeThemeMode,
        getThemeModeSnapshot,
        getServerThemeModeSnapshot,
    );

    const setThemeMode = useCallback( ( nextThemeMode: ThemeMode ) => {
        applyThemeMode( nextThemeMode );
        notifyThemeModeChange();
    }, [] );

    const toggleThemeMode = useCallback( () => {
        const nextThemeMode = themeMode === "dark" ? "light" : "dark";
        setThemeMode( nextThemeMode );
    }, [ setThemeMode, themeMode ] );

    const value = useMemo<ThemeContextValue>(
        () => ( {
            themeMode,
            setThemeMode,
            toggleThemeMode,
        } ),
        [ setThemeMode, themeMode, toggleThemeMode ],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeMode() {
    const context = useContext( ThemeContext );
    if ( !context ) {
        throw new Error( "useThemeMode must be used within ThemeProvider" );
    }

    return context;
}
